import { useCallback, useEffect, useRef, useState } from "react";
import type { AutocompleteHookReturn, AutocompleteItem, AutocompletePosition } from "./types";

export const useAutocomplete = (
	value: string,
	items: AutocompleteItem[],
	isReady: boolean = true,
	isValueSelected: boolean = false,
	onInputChange: (value: string) => void,
	onItemSelect: (selectedId: string) => void,
	onClear: () => void,
	onValueSelectedChange?: (isSelected: boolean) => void,
): AutocompleteHookReturn => {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [dropdownPosition, setDropdownPosition] = useState<AutocompletePosition>({ top: 0, left: 0, width: 0 });
	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Update dropdown position relative to input
	const updateDropdownPosition = useCallback(() => {
		if (inputRef.current) {
			const rect = inputRef.current.getBoundingClientRect();
			setDropdownPosition({
				top: rect.bottom + window.scrollY + 4, // Add small gap
				left: rect.left + window.scrollX,
				width: rect.width,
			});
		}
	}, []);

	// Handle input changes and manage dropdown state
	const handleInputChangeWithDropdown = useCallback(
		(inputValue: string) => {
			// If user starts typing after a selection, reset the isValueSelected flag
			if (isValueSelected && onValueSelectedChange) {
				onValueSelectedChange(false);
			}

			onInputChange(inputValue);
			// Open dropdown when there's input and suggestions might be available
			// but not if we just selected a value
			if (inputValue.trim() && isReady && !isValueSelected) {
				setIsDropdownOpen(true);
				updateDropdownPosition();
			} else {
				setIsDropdownOpen(false);
			}
		},
		[isValueSelected, onValueSelectedChange, onInputChange, isReady, updateDropdownPosition],
	);

	// Handle item selection
	const handleItemSelect = useCallback(
		(selectedId: string) => {
			const selectedItem = items.find(item => item.id === selectedId);
			if (selectedItem && !selectedItem.isPlaceholder) {
				onItemSelect(selectedId);
				setIsDropdownOpen(false);
				if (onValueSelectedChange) {
					onValueSelectedChange(true);
				}
			}
		},
		[items, onItemSelect, onValueSelectedChange],
	);

	// Handle clear input
	const handleClearInput = useCallback(() => {
		onClear();
		setIsDropdownOpen(false);
		if (onValueSelectedChange) {
			onValueSelectedChange(false);
		}
	}, [onClear, onValueSelectedChange]);

	// Handle input focus
	const handleInputFocus = useCallback(() => {
		if (typeof value === "string" && value?.trim() && items.length > 0 && isReady && !isValueSelected) {
			setIsDropdownOpen(true);
			updateDropdownPosition();
		}
	}, [value, items, isReady, isValueSelected, updateDropdownPosition]);

	// Close dropdown when suggestions are empty or loading is complete with no results
	useEffect(() => {
		if (items.length === 0 || (items.length === 1 && items[0].isPlaceholder)) {
			if (!value || typeof value !== "string" || !value.trim()) {
				setIsDropdownOpen(false);
			}
		} else if (items.length > 0 && typeof value === "string" && value.trim() && isReady && !isValueSelected) {
			setIsDropdownOpen(true);
			updateDropdownPosition();
		}
	}, [items, value, isReady, isValueSelected, updateDropdownPosition]);

	// Handle scroll and resize to update position
	useEffect(() => {
		const handleScrollOrResize = () => {
			if (isDropdownOpen) {
				updateDropdownPosition();
			}
		};

		// Use passive listeners for better performance
		window.addEventListener("scroll", handleScrollOrResize, { passive: true });
		window.addEventListener("resize", handleScrollOrResize, { passive: true });

		return () => {
			window.removeEventListener("scroll", handleScrollOrResize);
			window.removeEventListener("resize", handleScrollOrResize);
		};
	}, [isDropdownOpen, updateDropdownPosition]);

	// Handle escape key
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, []);

	// Handle click outside to close dropdown
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				inputRef.current &&
				!dropdownRef.current.contains(event.target as Node) &&
				!inputRef.current.contains(event.target as Node)
			) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	return {
		inputRef,
		dropdownRef,
		isDropdownOpen,
		dropdownPosition,
		handleInputChangeWithDropdown,
		handleItemSelect,
		handleClearInput,
		handleInputFocus,
	};
};
